import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Button, Modal, Tooltip, Typography } from 'antd'
import Icon, {
  ControlOutlined,
  AppstoreAddOutlined,
  UserSwitchOutlined,
  TagsOutlined,
  PhoneOutlined,
  ContainerOutlined,
  FileDoneOutlined,
  HomeOutlined,
  LaptopOutlined,
  HddOutlined,
  ClockCircleOutlined,
  ShareAltOutlined,
  ImportOutlined,
  GlobalOutlined,
  UsergroupAddOutlined,
  ApiOutlined,
  FieldTimeOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'

import { track } from '../../services/analytics/analytics'
import { getAnalyticsProperties } from '../../util/analytics-helpers'

import { Notification } from '../../components/custom-svg-icons'
import IntlMessages from '../../util/IntlMessages'

import { IPricePlanCard } from './types'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { BillingTypesEnum, USER_PLAN_LIMIT } from '@vacationtracker/shared/types/billing'
import { IPrice, PriceName } from '@vacationtracker/shared/data/stripe/prices'
import { NUMBER_OF_LOCATIONS_LIMIT, NUMBER_OF_TEAMS_LIMIT } from '@vacationtracker/shared/data/app-parameters'
import { FrontendUrls } from '../../types/urls'
import { calculateTotalPrice } from '../../util/billing'
import { ProductService } from '@vacationtracker/shared/data/stripe/product-service'
import { useCurrency } from '../../hooks/use-currency'

const { Text, Paragraph } = Typography

function getPriceDisplay(price: IPrice, totalUsers: number) {
  if (price.plan === SubscriptionPlanEnum.freemium) {
    return { pricePerUser: 0, totalPrice: 0, forUsers: 0 }
  }
  const effectiveUsers = Math.max(totalUsers, USER_PLAN_LIMIT)
  const pricePerUser = price.pricePerUser
  const totalPrice = calculateTotalPrice(price, effectiveUsers)
  return { pricePerUser, totalPrice, forUsers: effectiveUsers }
}

const PricePlanCard = ({
  plan,
  price,
  currentPeriod,
  totalUsers,
  currentPlan,
  newPeriod,
  showRevertingInfo,
  onSelectPLan,
  billingType = BillingTypesEnum.payAsYouGo,
  isSignup = false,
  isResubscribe = false,
  signupButtonText,
  isTrialPeriod = false,
  forceDisable = false,
  disableLimitTeam = false,
  disableLimitSubdepartments = false,
  totalSubdeparments = 0,
  disableLimitLocation = false,
  disableLimitWorkHours = false,
  disableMultiLevelApproval = false,
  disableNotificationWatchers = false,
  disableLimitLeaveTypes = false,
  disableTooltipMessage,
  subscriptionCancelAtPeriodEnd = false,
  source,
  analyticsOverrides,
  isLegacyPlanCustomer = false,
  footerExtra,
}: IPricePlanCard): React.ReactElement => {
  const { formatPrice } = useCurrency()
  const [showCoreRequiredActionsDetails, setShowCoreRequiredActionsDetails] = useState(false)
  const [showFreemiumRequiredActionsDetails, setShowFreemiumRequiredActionsDetails] = useState(false)

  const { pricePerUser: basePricePerUser, totalPrice: baseTotalPrice, forUsers } = getPriceDisplay(price, totalUsers)
  const isFreemium = plan === SubscriptionPlanEnum.freemium
  const isInfinityBucket = false
  const effectiveUsers = Math.max(totalUsers, USER_PLAN_LIMIT)
  const tierInfo = price.period === 'annual' ? ProductService.getAnnualTierInfo(price, effectiveUsers) : null
  const tierValues = tierInfo ? { tierMin: tierInfo.tierMin, tierMax: tierInfo.tierMax === 'inf' ? '∞' : tierInfo.tierMax } : {}

  // Legacy plan customers see their original locked pricing on Core Monthly:
  // $25/month for up to 50 users, $1/user above 50
  const isLegacyCoreMonthly = isLegacyPlanCustomer && plan === 'Core' && price.period === 'monthly'
  const pricePerUser = isLegacyCoreMonthly ? 1 : basePricePerUser
  const totalPrice = isLegacyCoreMonthly ? (effectiveUsers <= 50 ? 25 : effectiveUsers * 1) : baseTotalPrice
  const minUsersMessageId = isLegacyCoreMonthly ? 'components.pricePlanCard.totalPriceForLessThan50Users' : 'components.pricePlanCard.totalPriceForLessThan25Users'

  const getDowngradeToCoreActionsModal = () => {
    return (
      <Modal
        open={showCoreRequiredActionsDetails}
        centered
        onCancel={() => setShowCoreRequiredActionsDetails(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setShowCoreRequiredActionsDetails(false)}>
            <IntlMessages id="app.gotIt" />
          </Button>,
        ]}
        title={<IntlMessages id="components.pricePlanCard.disableDowngradeToCoreTitle" />}
      >
        <Paragraph>
          <IntlMessages id="components.pricePlanCard.disableDowngradeToCoreDescription" />
        </Paragraph>
        {disableLimitLocation && (
          <Paragraph>
            <Link to={'/app/settings/locations'}>
              <IntlMessages id="app.locations" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToCoreReasonLocations" />
          </Paragraph>
        )}
        {disableLimitTeam && (
          <Paragraph>
            <Link to={'/app/settings/departments'}>
              <IntlMessages id="app.departments" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToCoreReasonDepartments" />
          </Paragraph>
        )}
        {disableMultiLevelApproval && (
          <Paragraph>
            <Link to={'/app/settings/departments'}>
              <IntlMessages id="app.departments" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToMultiLevelApproval" />
          </Paragraph>
        )}
        {disableNotificationWatchers && (
          <Paragraph>
            <Link to={'/app/settings/departments'}>
              <IntlMessages id="app.departments" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToNotificationWatchers" />
          </Paragraph>
        )}
        {disableLimitSubdepartments && (
          <Paragraph>
            <Link to={'/app/settings/departments'}>
              <IntlMessages id="app.departmentsSub" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToCoreReasonSubdepartments" values={{ total: totalSubdeparments }} />
          </Paragraph>
        )}
        {disableLimitWorkHours && (
          <Paragraph>
            <Link to={'/app/settings/locations'}>
              <IntlMessages id="app.customWorkHours" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToCoreReasonWorkday" />
          </Paragraph>
        )}
        <Paragraph>
          <IntlMessages id="components.helpDrawer.supportInfo" values={{ link: (...chunks) => <a href="mailto:hello@vacationtracker.io">{chunks}</a> }} />
        </Paragraph>
      </Modal>
    )
  }

  const getDowngradeToFreemiumActionsModal = () => {
    return (
      <Modal
        open={showFreemiumRequiredActionsDetails}
        centered
        onCancel={() => setShowFreemiumRequiredActionsDetails(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setShowFreemiumRequiredActionsDetails(false)}>
            <IntlMessages id="app.gotIt" />
          </Button>,
        ]}
        title={<IntlMessages id="components.pricePlanCard.disableDowngradeToFreemiumTitle" />}
      >
        <Paragraph>
          <IntlMessages id="components.pricePlanCard.disableDowngradeToFreemiumDescription" />
        </Paragraph>
        {disableLimitLocation && (
          <Paragraph>
            <Link to={'/app/settings/locations'}>
              <IntlMessages id="app.locations" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToFreemiumReasonLocations" />
          </Paragraph>
        )}
        {disableLimitTeam && (
          <Paragraph>
            <Link to={'/app/settings/departments'}>
              <IntlMessages id="app.departments" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToFreemiumReasonDepartments" />
          </Paragraph>
        )}
        {disableMultiLevelApproval && (
          <Paragraph>
            <Link to={'/app/settings/departments'}>
              <IntlMessages id="app.departments" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToMultiLevelApproval" />
          </Paragraph>
        )}
        {disableNotificationWatchers && (
          <Paragraph>
            <Link to={'/app/settings/departments'}>
              <IntlMessages id="app.departments" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToNotificationWatchers" />
          </Paragraph>
        )}
        {disableLimitSubdepartments && (
          <Paragraph>
            <Link to={'/app/settings/departments'}>
              <IntlMessages id="app.departmentsSub" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToFreemiumReasonSubdepartments" values={{ total: totalSubdeparments }} />
          </Paragraph>
        )}
        {disableLimitWorkHours && (
          <Paragraph>
            <Link to={'/app/settings/locations'}>
              <IntlMessages id="app.customWorkHours" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToFreemiumReasonWorkday" />
          </Paragraph>
        )}
        {disableLimitLeaveTypes && (
          <Paragraph>
            <Link to={'/app/settings/leave-types'}>
              <IntlMessages id="app.leaveType" />:{' '}
            </Link>
            <IntlMessages id="components.pricePlanCard.disableDowngradeToFreemiumReasonleaveType" />
          </Paragraph>
        )}
        <Paragraph>
          <IntlMessages id="components.helpDrawer.supportInfo" values={{ link: (...chunks) => <a href="mailto:hello@vacationtracker.io">{chunks}</a> }} />
        </Paragraph>
      </Modal>
    )
  }

  return (
    <div className={currentPlan === plan ? 'current-plan price-plan-card' : 'price-plan-card'}>
      {getDowngradeToCoreActionsModal()}
      {getDowngradeToFreemiumActionsModal()}
      <div className="card-header">
        <div className="plan-name-wrap">
          <span className="plan-name">{plan === SubscriptionPlanEnum.freemium ? <IntlMessages id="app.freemium" /> : plan}</span>
          {isLegacyPlanCustomer && plan === 'Core' && newPeriod === 'monthly' && (
            <span
              style={{
                marginLeft: '8px',
                padding: '2px 8px',
                backgroundColor: '#52c41a',
                color: 'white',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              🔒 <IntlMessages id="components.billing.lockedPrice" />
            </span>
          )}
        </div>
        {plan !== 'Enterprise' ? (
          <>
            <div className="plan-price-header">
              {isFreemium ? (
                <IntlMessages
                  id="components.pricePlanCard.freemium.pricePerUser"
                  values={{
                    price: (...chunks) => <span className="price">{chunks}</span>,
                    formattedPrice: formatPrice(0),
                  }}
                />
              ) : price.period === 'monthly' ? (
                <IntlMessages
                  id={billingType === BillingTypesEnum.seatBased ? 'components.pricePlanCard.pricePerSeat' : 'components.pricePlanCard.pricePerUser'}
                  values={{
                    pricePerUser: formatPrice(pricePerUser),
                    newPricePerUser: formatPrice(pricePerUser),
                    price: (...chunks) => <span className="price">{chunks}</span>,
                    oldPrice: (...chunks) => chunks,
                    newPrice: () => '',
                  }}
                />
              ) : (
                <IntlMessages
                  id="components.pricePlanCard.totalPricePerUser"
                  values={{
                    totalPrice: formatPrice(totalPrice),
                    newTotalPrice: formatPrice(totalPrice),
                    price: (...chunks) => <span className="price">{chunks}</span>,
                    oldPrice: (...chunks) => chunks,
                    newPrice: () => '',
                  }}
                />
              )}
            </div>
            <p className="plan-price-total-minimum">
              {isFreemium ? (
                <IntlMessages id="components.pricePlanCard.freemium.unlimitedUsers" />
              ) : isSignup ? (
                <IntlMessages
                  id="components.pricePlanCard.minimumPlanPrice"
                  values={{
                    totalPrice: formatPrice(totalPrice),
                    newTotalPrice: formatPrice(totalPrice),
                    price: (...chunks) => <span className="price">{chunks}</span>,
                    oldPrice: (...chunks) => chunks,
                    newPrice: () => '',
                  }}
                />
              ) : isInfinityBucket && price.period === 'annual' ? (
                <IntlMessages id="components.pricePlanCard.forInfinityUsers" values={{ pricePerUser: formatPrice(totalUsers > 0 ? Math.round(totalPrice / effectiveUsers) : 0) }} />
              ) : totalUsers > USER_PLAN_LIMIT ? (
                <IntlMessages
                  id={price.period === 'annual' ? 'components.pricePlanCard.totalPriceAnnual' : 'components.pricePlanCard.totalPrice'}
                  values={{
                    totalUsers: price.period === 'annual' ? forUsers : effectiveUsers,
                    totalPrice: formatPrice(totalPrice),
                    newTotalPrice: formatPrice(totalPrice),
                    price: (...chunks) => <span className="price">{chunks}</span>,
                    oldPrice: (...chunks) => chunks,
                    newPrice: () => '',
                    ...tierValues,
                  }}
                />
              ) : (
                <IntlMessages
                  id={price.period === 'annual' ? 'components.pricePlanCard.forUsers' : minUsersMessageId}
                  values={{
                    totalPrice: formatPrice(totalPrice),
                    newTotalPrice: formatPrice(totalPrice),
                    users: forUsers,
                    price: (...chunks) => <span className="price">{chunks}</span>,
                    oldPrice: (...chunks) => chunks,
                    newPrice: () => '',
                    ...tierValues,
                  }}
                />
              )}
              {tierInfo && (
                <>
                  {' '}
                  <Tooltip
                    title={
                      <>
                        <IntlMessages
                          id="components.pricePlanCard.annualTierTooltip"
                          values={{
                            totalUsers: effectiveUsers,
                            link: (...chunks) => (
                              <a
                                href={`https://vacationtracker.io/pricing?${new URLSearchParams({ users: effectiveUsers.toString(), period: 'annual' })}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#fff', textDecoration: 'underline' }}
                              >
                                {chunks}
                              </a>
                            ),
                          }}
                        />
                        {![PriceName.CORE_ANNUAL_2025, PriceName.COMPLETE_ANNUAL_2025].includes(price.name) && (
                          <>
                            {' '}
                            <IntlMessages id="components.pricePlanCard.annualTierTooltipLegacy" />
                          </>
                        )}
                      </>
                    }
                  >
                    <QuestionCircleOutlined style={{ cursor: 'pointer' }} />
                  </Tooltip>
                </>
              )}
            </p>
          </>
        ) : (
          <p className="price-contact-us">
            <IntlMessages id="components.pricePlanCard.contactUs" />
          </p>
        )}
      </div>
      <div className="card-body">
        {plan === 'Freemium' && (
          <>
            <p>
              <LaptopOutlined /> <IntlMessages id="components.pricePlanCard.managePTO" />
            </p>
            <p>
              <HomeOutlined /> <IntlMessages id="components.pricePlanCard.freemium.oneLocation" />
            </p>
            <p>
              <HddOutlined /> <IntlMessages id="components.pricePlanCard.freemium.oneDepartment" />
            </p>
            <p>
              <FileDoneOutlined /> <IntlMessages id="components.pricePlanCard.freemium.oneLeavePolicy" />
            </p>
            <p>
              <Icon component={Notification} /> <IntlMessages id="components.pricePlanCard.freemium.oneAdmin" />
            </p>
            <p>
              <Icon component={Notification} /> <IntlMessages id="components.pricePlanCard.notifications" />
            </p>
          </>
        )}
        {plan === 'Core' && (
          <>
            <p>
              <LaptopOutlined /> <IntlMessages id="components.pricePlanCard.managePTO" />
            </p>
            <p>
              <HomeOutlined /> <IntlMessages id="components.pricePlanCard.coreDepartmentsAndLocations" />
            </p>
            <p>
              <HddOutlined /> <IntlMessages id="components.pricePlanCard.customizeLeaveTypes" />
            </p>
            <p>
              <FileDoneOutlined /> <IntlMessages id="components.pricePlanCard.reports" />
            </p>
            <p>
              <Icon component={Notification} /> <IntlMessages id="components.pricePlanCard.notifications" />
            </p>
            <p>
              <ApiOutlined /> <IntlMessages id="components.pricePlanCard.openApi" />
            </p>
            <p>
              <FieldTimeOutlined /> <IntlMessages id="components.pricePlanCard.leaveRequestNoticePeriod" />
            </p>
          </>
        )}
        {plan === 'Complete' && (
          <>
            <p>
              <AppstoreAddOutlined />
              <IntlMessages id="components.pricePlanCard.EverythingInTheCorePlan" />
            </p>
            <p>
              <HomeOutlined /> <IntlMessages id="components.pricePlanCard.unlimitedDepartmentsAndLocations" />
            </p>
            <p>
              <ControlOutlined /> <IntlMessages id="components.pricePlanCard.accruals" />
            </p>
            <p>
              <UsergroupAddOutlined /> <IntlMessages id="components.pricePlanCard.multilevelApproval" />
            </p>
            <p>
              <ClockCircleOutlined /> <IntlMessages id="components.pricePlanCard.hourlyLeaves" />
            </p>
            <p>
              <TagsOutlined /> <IntlMessages id="components.pricePlanCard.labels" />
            </p>
            <p>
              <ContainerOutlined /> <IntlMessages id="components.pricePlanCard.reportsSchedule" />
            </p>
            <p>
              <PhoneOutlined /> <IntlMessages id="components.pricePlanCard.support" />
            </p>
            <p>
              <ImportOutlined /> <IntlMessages id="app.timeOffInLieu" />
            </p>
            <p>
              <ShareAltOutlined /> <IntlMessages id="components.pricePlanCard.sharedCalendars" />
            </p>
            <p>
              <UserSwitchOutlined /> <IntlMessages id="components.pricePlanCard.substituteApprovers" />
            </p>
            <p>
              <GlobalOutlined /> <IntlMessages id="components.pricePlanCard.workday" />
            </p>
            <p>
              <ApiOutlined /> <IntlMessages id="components.pricePlanCard.openApi" />
            </p>
            <p className="see-more-addons">
              <IntlMessages
                id="components.pricePlanCard.addonsWithExamples"
                values={{
                  seeMore: (...chunks) => (
                    <Link
                      to={FrontendUrls.addons}
                      target="_blank"
                      onClick={() => {
                        track('COMPLETE_FEATURES_EXPANDED', {
                          ...getAnalyticsProperties(analyticsOverrides),
                        })
                      }}
                    >
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </p>
          </>
        )}
      </div>
      <div className="card-footer">
        {plan !== 'Enterprise' && !isSignup && (
          <Tooltip title={subscriptionCancelAtPeriodEnd ? <IntlMessages id="billing.resubscribeToChangePlans" /> : disableTooltipMessage || null}>
            <Button
              disabled={(currentPlan === plan && currentPeriod === newPeriod) || (currentPlan === 'Freemium' && plan === 'Freemium') || forceDisable}
              size="large"
              type="primary"
              onClick={() => {
                const targetPlan =
                  currentPlan === 'Core' && currentPeriod === 'annual' && plan === 'Complete' && newPeriod === 'monthly' && !isTrialPeriod && !subscriptionCancelAtPeriodEnd
                    ? 'support'
                    : plan

                track('PLAN_SWITCH_BUTTON_CLICKED', {
                  ...getAnalyticsProperties(analyticsOverrides),
                  plan: targetPlan,
                  source: source || 'billing_page',
                })

                onSelectPLan(targetPlan)
              }}
              className={(currentPlan === plan && currentPeriod === newPeriod) || (currentPlan === 'Freemium' && plan === 'Freemium') ? 'btn-disabled' : ''}
            >
              {currentPlan === 'Core' && currentPeriod === 'annual' && plan === 'Complete' && newPeriod === 'monthly' && !isTrialPeriod && !isResubscribe ? (
                <IntlMessages id="components.pricePlanCard.contactUs" />
              ) : (
                <>
                  {currentPlan === 'Freemium' && plan === 'Freemium' ? (
                    <IntlMessages id="components.pricePlanCard.currentPlan" />
                  ) : (
                    <>
                      {currentPlan === plan && currentPeriod === newPeriod ? (
                        <IntlMessages id="components.pricePlanCard.currentPlan" />
                      ) : (
                        <IntlMessages
                          id="components.pricePlanCard.switchPlan"
                          values={{
                            newPeriod: `${newPeriod && newPeriod.charAt(0).toUpperCase() + newPeriod.slice(1)}`,
                            period: (...chunks) => (currentPeriod === newPeriod ? '' : chunks),
                          }}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </Button>
          </Tooltip>
        )}
        {plan === 'Core' && currentPlan !== 'Core' && (disableLimitLocation || disableLimitTeam) && (
          <Alert
            message={
              <>
                <IntlMessages
                  id={
                    disableLimitLocation && disableLimitTeam
                      ? 'components.pricePlanCard.disableDowngradeToCoreBoth'
                      : disableLimitLocation
                        ? 'components.pricePlanCard.disableDowngradeToCoreLocationsOnly'
                        : 'components.pricePlanCard.disableDowngradeToCoreDepartmentsOnly'
                  }
                  values={{
                    noOfLocations: NUMBER_OF_LOCATIONS_LIMIT,
                    noOfDepartments: NUMBER_OF_TEAMS_LIMIT,
                  }}
                />
              </>
            }
            style={{ marginTop: '10px' }}
            type="warning"
          />
        )}

        {plan === 'Freemium' && currentPlan !== 'Freemium' && (disableLimitLocation || disableLimitTeam || disableLimitLeaveTypes) && (
          <Alert
            message={
              <>
                <IntlMessages
                  id="components.pricePlanCard.disableDowngradeToFreemium"
                  values={{
                    noOfActions: [
                      disableLimitLocation,
                      disableLimitSubdepartments,
                      disableLimitTeam,
                      disableLimitWorkHours,
                      disableMultiLevelApproval,
                      disableNotificationWatchers,
                      disableLimitLeaveTypes,
                    ].filter(Boolean).length,
                  }}
                />
              </>
            }
            style={{ marginTop: '10px' }}
            type="warning"
          />
        )}

        {plan === 'Enterprise' && (
          <Button
            disabled={currentPlan === plan || forceDisable}
            size="large"
            type="primary"
            onClick={() => onSelectPLan(plan)}
            className={currentPlan === plan ? 'btn-disabled' : ''}
          >
            <IntlMessages id="components.pricePlanCard.scheduleAConsultation" />
          </Button>
        )}
        {isSignup && (
          <Button
            size="large"
            type="primary"
            data-qa={`select-${plan === SubscriptionPlanEnum.complete ? SubscriptionPlanEnum.complete : SubscriptionPlanEnum.core}-plan`}
            onClick={() => onSelectPLan(plan)}
          >
            {signupButtonText}
          </Button>
        )}
        {!isSignup && !isResubscribe && showRevertingInfo && (
          <div className="change-price-info">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {plan === 'Complete' && currentPlan !== plan && currentPeriod === newPeriod && !isTrialPeriod && <IntlMessages id="components.pricePlanCard.upgradePlan" />}
              {(currentPlan !== plan || newPeriod !== currentPeriod) && isTrialPeriod && plan !== 'Enterprise' && plan !== 'Freemium' && (
                <IntlMessages id="components.pricePlanCard.duringTheTrial" />
              )}
              {isTrialPeriod && plan === 'Freemium' && <IntlMessages id="components.pricePlanCard.losingTrialOnFreemiumPlan" />}
              {plan === 'Core' && currentPlan === 'Complete' && currentPeriod === newPeriod && !isTrialPeriod && <IntlMessages id="components.pricePlanCard.downgradePlan" />}
              {plan === 'Core' && currentPlan === 'Freemium' && !isTrialPeriod && <IntlMessages id="components.pricePlanCard.upgradePlan" />}
            </Text>
          </div>
        )}
        {footerExtra}
      </div>
    </div>
  )
}

export default PricePlanCard
